import { useState, useEffect } from "react";
import AboutPage from "./components/About";
import Navbar from "./components/Navbar";
import HomePage from "./components/Home";
import ContactPage from "./components/Contact";
import { motion, AnimatePresence } from "framer-motion";

// Helper for smooth scrolling
const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};
console.log(scrollToSection);

// --- Main App Component ---
const App = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [theme, setTheme] = useState("light"); // 'light' or 'dark'

  useEffect(() => {
    // Set initial theme from localStorage or system preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.add(savedTheme);
    } else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      setTheme("dark");
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.classList.remove(theme);
    document.documentElement.classList.add(newTheme);
  };

  // Global styles for 'Playfair Display' font and smooth scrolling
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    // Load Playfair Display font from Google Fonts
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    document.documentElement.style.fontFamily = '"Playfair Display", serif';

    // Add keyframes for gradient text and background animations
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = `
      @keyframes gradient-text {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .animate-gradient-text {
        background-size: 200% auto;
        animation: gradient-text 3s linear infinite;
      }

      @keyframes gradient-slow {
        0% { background-position: 0% 50%; }
        100% { background-position: 100% 50%; }
      }
      .animate-gradient-slow {
        background-size: 400% 400%;
        animation: gradient-slow 15s ease infinite alternate;
      }

      @keyframes gradient-fast {
        0% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .animate-gradient-fast {
        background-size: 400% 400%;
        animation: gradient-fast 10s ease infinite alternate-reverse;
      }

      /* Base styles for dark mode transition */
      .dark {
        background-color: #1a202c; /* Tailwind gray-900 */
        color: #e2e8f0; /* Tailwind gray-200 */
      }
      body {
        transition: background-color 0.3s ease, color 0.3s ease;
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
      document.head.removeChild(link); // Clean up the font link
    };
  }, []);

  return (
    <div className={`min-h-screen ${theme === "dark" ? "dark" : ""}`}>
      <Navbar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        toggleTheme={toggleTheme}
        theme={theme}
      />
      <main>
        <AnimatePresence mode="wait">
          {currentPage === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <HomePage setCurrentPage={setCurrentPage} theme={theme} />
            </motion.div>
          )}
          {currentPage === "about" && (
            <motion.div
              key="about"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <AboutPage theme={theme} /> {/* Pass theme prop */}
            </motion.div>
          )}
          {currentPage === "contact" && (
            <motion.div
              key="contact"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ContactPage theme={theme} /> {/* Pass theme prop */}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default App;
